import React, { useCallback } from 'react';
import { RemoteDataUI } from '../../../common/ui/RemoteDataUI';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { useFilterContext } from "../infrastructure/FilterContext";
import { serializeFiltersToQuery } from "../infrastructure/filterUrlMapper";
import { GtmService } from "../../../infrastructure/analytics/GtmService";
import { FloatingDropdown } from "../../../ui/components/FloatingDropdown";
import { useHasNewProducts } from "../hooks/useHasNewProducts";
import { useTenantContext } from "../../../tenant/TenantContext";
import styled from "styled-components";

/**
 * Constants for styling
 */
const FONT_SIZE_SMALL = '0.8em';
const MARGIN_LEFT = '0.9375rem';
const MARGIN_TOP = '0.3125rem';

/**
 * Styled component for dropdown titles
 */
const Title = styled.span`
    font-size: ${FONT_SIZE_SMALL};
    margin-left: ${MARGIN_LEFT};
    margin-top: ${MARGIN_TOP};
    font-weight: 400;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    @media (max-width: 1280px) {
        font-size: 0.7em;
    }
`;

// Color de "oferta" por defecto; cada tenant puede traer el suyo (promoColor en la config).
const PROMO_ORANGE = '#f0541e';

const TitlePill = styled(Title)`
    background: ${({ $bg }) => $bg};
    color: white;
    border-radius: 999px;
    padding: 0.25rem 0.9rem;

    &:hover {
        text-decoration: none;
        opacity: 0.85;
    }
`;

export const SearchLinksFilter = () => {
    const { filterOptionsRemoteData, updateFilters, filters } = useFilterContext();
    const navigate = useNavigate();
    const hasNewProducts = useHasNewProducts();
    const { primaryColor, promoColor } = useTenantContext();

    const onClick = useCallback((taxonomyFilter, navLabel) => {
        if (navLabel) GtmService.logNavClick(navLabel);
        // La categoria del menu se COMBINA con la busqueda y filtros activos (decision
        // usuario 12-ago): mismo comportamiento que elegirla en el panel lateral. Solo se
        // sustituye la categoria anterior.
        const { taxonomyTree: _previous, ...rest } = filters || {};
        const newFilters = { ...rest, taxonomyTree: taxonomyFilter };
        updateFilters(newFilters);

        // URL final en un solo salto, siempre: la pareja push-sin-query + replace posterior
        // dejaba estados viejos en el historial que podian resucitar.
        const query = serializeFiltersToQuery(newFilters).toString();
        navigate(query ? `/articulos?${query}` : '/articulos');
    }, [navigate, updateFilters, filters]);


    const onChangeMacrofamily = useCallback((macrofamilyId, navLabel) => {
        onClick({
            macrofamilyId: macrofamilyId,
            familyId: null,
            subfamilyIds: new Set()
        }, navLabel);
    }, [onClick]);

    const onChangeFamily = useCallback((macrofamilyId, familyId, navLabel) => {
        onClick({
            macrofamilyId: macrofamilyId,
            familyId: familyId,
            subfamilyIds: new Set()
        }, navLabel);
    }, [onClick]);

    const onChangeSubfamily = useCallback((macrofamilyId, familyId, subfamilyId, navLabel) => {
        onClick({
            macrofamilyId: macrofamilyId,
            familyId: familyId,
            subfamilyIds: new Set([subfamilyId])
        }, navLabel);
    }, [onClick]);

    return (
        <RemoteDataUI remoteData={filterOptionsRemoteData}>
            <RemoteDataUI.Pending>
                <div className="d-flex justify-content-center align-items-center">
                    <Spinner size="sm" />
                </div>
            </RemoteDataUI.Pending>

            <RemoteDataUI.Success>
                {
                    filterOptions => (
                        <div className="row justify-content-around align-items-center">
                            {filterOptions.macrofamilias.map((macrofamily) => (
                                <div className="col-12 col-sm-6 col-md-auto" key={macrofamily.id}>
                                    <FloatingDropdown>
                                        <FloatingDropdown.Title>
                                            <Title
                                                className="fw-bold fs-6"
                                                onClick={() => onChangeMacrofamily(macrofamily.id, macrofamily.macrofamilia)}
                                            >
                                                {macrofamily.macrofamilia}
                                            </Title>
                                        </FloatingDropdown.Title>
                                        <FloatingDropdown.Items>
                                            {macrofamily.familias.map((family) => (
                                                <FloatingDropdown key={family.id} placement="after">
                                                    <FloatingDropdown.Title>
                                                        <Title
                                                            onClick={() =>
                                                                onChangeFamily(macrofamily.id, family.id,
                                                                    `${macrofamily.macrofamilia} > ${family.familia}`)
                                                            }
                                                        >
                                                            {family.familia}
                                                        </Title>
                                                    </FloatingDropdown.Title>
                                                    <FloatingDropdown.Items>
                                                        {family.subfamilias.map((subfamily) => (
                                                            <Title
                                                                key={subfamily.id}
                                                                onClick={() =>
                                                                    onChangeSubfamily(macrofamily.id, family.id, subfamily.id,
                                                                        `${macrofamily.macrofamilia} > ${family.familia} > ${subfamily.subfamilia}`)
                                                                }
                                                            >
                                                                {subfamily.subfamilia}
                                                            </Title>
                                                        ))}
                                                    </FloatingDropdown.Items>
                                                </FloatingDropdown>
                                            ))}
                                        </FloatingDropdown.Items>
                                    </FloatingDropdown>
                                </div>
                            ))}
                            {hasNewProducts && (
                                <div className="col-12 col-sm-6 col-md-auto">
                                    <TitlePill
                                        $bg={primaryColor}
                                        className="fw-bold fs-6"
                                        onClick={() => { GtmService.logNavClick('Nuevos'); navigate('/nuevos'); }}
                                    >
                                        Nuevos
                                    </TitlePill>
                                </div>
                            )}
                            <div className="col-12 col-sm-6 col-md-auto">
                                <TitlePill
                                    $bg={promoColor || PROMO_ORANGE}
                                    className="fw-bold fs-6"
                                    onClick={() => { GtmService.logNavClick('Ofertas'); navigate('/promociones'); }}
                                >
                                    Ofertas
                                </TitlePill>
                            </div>
                        </div>

                    )
                }
            </RemoteDataUI.Success>
        </RemoteDataUI>
    );

};